import * as React from "react"

import { cn } from "cn"
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxValue,
  useComboboxAnchor,
} from "@/components/ui/combobox"

/** Synthetic event for legacy MUI Autocomplete-style `(event, value)` callbacks. */
export function comboboxChangeEvent(): React.SyntheticEvent {
  return {
    type: "change",
    preventDefault() { },
    stopPropagation() { },
  } as React.SyntheticEvent
}

export const formComboboxInputClassName =
  "w-full min-w-0 bg-white"

type CreatableConfig = {
  /** Option label shown in the list (e.g. `Add New: "foo"`). */
  formatLabel: (query: string) => string
  /**
   * Value applied when the creatable row is selected.
   * Defaults to `formatLabel(query)` (Gender / Language / Genre pattern).
   */
  toValue?: (query: string) => string
  /**
   * When to suppress the creatable row:
   * - `exact` (default): skip only if an option equals the query (case-insensitive),
   *   matching Gender/Language/Genre's old MUI `inputValue === option` check.
   * - `includes`: skip if any option contains the query (case-insensitive), so
   *   partials like "asm" don't compete with "ASMR" (ContentTagSelect).
   */
  match?: "exact" | "includes"
}

export interface StringFormComboboxProps {
  id?: string
  options: readonly string[]
  value?: string | null
  onChange: (value: string | null) => void
  creatable?: CreatableConfig | false
  disabled?: boolean
  readOnly?: boolean
  required?: boolean
  isErrored?: boolean
  placeholder?: string
  clearable?: boolean
  emptyText?: string
  autoComplete?: string
  className?: string
}

/**
 * Shared single-select combobox for string option lists used in edit/new forms.
 * Keyboard: arrow keys highlight, Enter selects highlighted row, Escape closes,
 * typing filters. Clear: optional clear button when `clearable`.
 * Creatable: optional "add new" row when the query matches no option.
 *
 * Close policy (creatable and non-creatable): blur / outside click / Escape
 * reverts the input to the last committed value (MUI `clearOnBlur`). Values
 * change only by choosing a list row — including the creatable row — or Enter
 * while that row is highlighted (`autoHighlight`). We do not freeSolo-commit
 * raw filter text on blur (avoids "asm" or "foobar" replacing a real option).
 */
export function StringFormCombobox(props: StringFormComboboxProps) {
  const {
    id,
    options,
    value,
    onChange,
    creatable = false,
    disabled,
    readOnly,
    required,
    isErrored,
    placeholder,
    clearable = !readOnly,
    emptyText = "No results.",
    autoComplete = "off",
    className,
  } = props

  // Match stored values to options case-insensitively (ContentTagSelect used MUI
  // isOptionEqualToValue the same way). Prefer the canonical option string.
  const resolvedValue = React.useMemo(() => {
    if (value == null || value === "") return null
    const match = options.find(
      (option) => option.toLowerCase() === value.toLowerCase()
    )
    return match ?? value
  }, [options, value])

  const committedLabel = resolvedValue ?? ""
  const committedLabelRef = React.useRef(committedLabel)
  committedLabelRef.current = committedLabel

  const [open, setOpen] = React.useState(false)
  const [inputValue, setInputValue] = React.useState(committedLabel)

  // Keep the visible input aligned when the form value changes externally.
  React.useEffect(() => {
    if (!open) setInputValue(committedLabel)
  }, [committedLabel, open])

  const items = React.useMemo(() => {
    const list = [...options]
    if (
      resolvedValue &&
      !list.some((option) => option.toLowerCase() === resolvedValue.toLowerCase())
    ) {
      list.push(resolvedValue)
    }
    const trimmed = inputValue.trim()
    if (creatable && trimmed) {
      const queryLower = trimmed.toLowerCase()
      const matchMode = creatable.match ?? "exact"
      const exists =
        matchMode === "includes"
          ? list.some((option) => option.toLowerCase().includes(queryLower))
          : list.some((option) => option.toLowerCase() === queryLower)
      if (!exists) {
        list.push(creatable.formatLabel(trimmed))
      }
    }
    return list
  }, [options, inputValue, creatable, resolvedValue])

  const resolveCreatableValue = (selected: string) => {
    if (!creatable) return selected
    const trimmed = inputValue.trim()
    if (!trimmed) return selected
    if (selected === creatable.formatLabel(trimmed)) {
      return creatable.toValue ? creatable.toValue(trimmed) : selected
    }
    return selected
  }

  const revertInputToCommitted = React.useCallback(() => {
    setInputValue(committedLabelRef.current)
  }, [])

  return (
    <Combobox
      items={items}
      value={resolvedValue}
      inputValue={inputValue}
      onValueChange={(next) => {
        if (next == null) {
          committedLabelRef.current = ""
          setInputValue("")
          onChange(null)
          return
        }
        const committed = resolveCreatableValue(next)
        committedLabelRef.current = committed
        setInputValue(committed)
        onChange(committed)
      }}
      onInputValueChange={setInputValue}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen)
        if (nextOpen) return
        // clearOnBlur: discard filter text; do not commit it as a new value.
        revertInputToCommitted()
      }}
      disabled={disabled}
      readOnly={readOnly}
      required={required}
      autoHighlight
      autoComplete={autoComplete}
    >
      <ComboboxInput
        id={id}
        placeholder={placeholder}
        disabled={disabled}
        showClear={clearable && !disabled && !readOnly}
        aria-invalid={isErrored || undefined}
        className={cn(
          formComboboxInputClassName,
          isErrored && "border-destructive",
          className
        )}
      />
      <ComboboxContent>
        <ComboboxEmpty>{emptyText}</ComboboxEmpty>
        <ComboboxList>
          {(item) => (
            <ComboboxItem key={item} value={item}>
              {item}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  )
}

export interface MultiStringFormComboboxProps {
  id?: string
  options: readonly string[]
  value: string[]
  onChange: (value: string[]) => void
  disabled?: boolean
  readOnly?: boolean
  required?: boolean
  isErrored?: boolean
  placeholder?: string
  emptyText?: string
  getOptionLabel?: (option: string) => string
  className?: string
}

/**
 * Shared multi-select combobox with chips.
 * Stays open while selecting; Backspace removes the last chip when the input is empty.
 */
export function MultiStringFormCombobox(props: MultiStringFormComboboxProps) {
  const {
    id,
    options,
    value,
    onChange,
    disabled,
    readOnly,
    required,
    isErrored,
    placeholder,
    emptyText = "No results.",
    getOptionLabel = (option) => option,
    className,
  } = props

  const anchor = useComboboxAnchor()

  return (
    <Combobox
      multiple
      items={[...options]}
      value={value}
      onValueChange={(next) => onChange(next ?? [])}
      disabled={disabled}
      readOnly={readOnly}
      required={required}
      autoHighlight
      itemToStringLabel={getOptionLabel}
    >
      <ComboboxChips
        ref={anchor}
        id={id}
        aria-invalid={isErrored || undefined}
        className={cn(
          "w-full bg-white",
          isErrored && "border-destructive",
          className
        )}
      >
        <ComboboxValue>
          {(values: string[]) => (
            <React.Fragment>
              {values.map((item) => (
                <ComboboxChip key={item}>{getOptionLabel(item)}</ComboboxChip>
              ))}
              <ComboboxChipsInput
                placeholder={values.length ? undefined : placeholder}
                disabled={disabled}
                readOnly={readOnly}
              />
            </React.Fragment>
          )}
        </ComboboxValue>
      </ComboboxChips>
      <ComboboxContent
        anchor={anchor}
      >
        <ComboboxEmpty>{emptyText}</ComboboxEmpty>
        <ComboboxList>
          {(item) => (
            <ComboboxItem key={item} value={item}>
              {getOptionLabel(item)}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  )
}
